import React, { useEffect, useMemo, useState } from "react";
import "../styles/referralAdminDashboard.css";

function formatMoney(cents = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((Number(cents) || 0) / 100);
}

function formatDate(ts) {
  if (!ts) return "—";
  if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleString();
  if (typeof ts === "string") return new Date(ts).toLocaleString();
  return "—";
}

function StatCard({ label, value, subtext }) {
  return (
    <div className="radCard">
      <div className="radStatLabel">{label}</div>
      <div className="radStatValue">{value}</div>
      {subtext ? <div className="radStatSubtext">{subtext}</div> : null}
    </div>
  );
}

function TableCard({ title, children, actions = null }) {
  return (
    <section className="radCard">
      <div className="radCardHeader">
        <div>
          <h2 className="radCardTitle">{title}</h2>
        </div>
        {actions ? <div className="radCardActions">{actions}</div> : null}
      </div>
      <div className="radTableWrap">{children}</div>
    </section>
  );
}

export default function ReferralAdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const [overviewRes, referrersRes, locationsRes, earningsRes] = await Promise.all([
          fetch("/api/admin/referrals/overview", { credentials: "include" }),
          fetch("/api/admin/referrals/referrers", { credentials: "include" }),
          fetch("/api/admin/referrals/locations", { credentials: "include" }),
          fetch("/api/admin/referrals/earnings", { credentials: "include" }),
        ]);

        const [overviewJson, referrersJson, locationsJson, earningsJson] = await Promise.all([
          overviewRes.json(),
          referrersRes.json(),
          locationsRes.json(),
          earningsRes.json(),
        ]);

        if (!mounted) return;

        if (!overviewRes.ok) throw new Error(overviewJson.error || "Failed to load overview");
        if (!referrersRes.ok) throw new Error(referrersJson.error || "Failed to load referrers");
        if (!locationsRes.ok) throw new Error(locationsJson.error || "Failed to load locations");
        if (!earningsRes.ok) throw new Error(earningsJson.error || "Failed to load earnings");

        setOverview(overviewJson);
        setReferrers(referrersJson.referrers || []);
        setLocations(locationsJson.locations || []);
        setEarnings(earningsJson.earnings || []);
      } catch (err) {
        console.error("Referral dashboard load error:", err);
        if (mounted) setError(err.message || "Failed to load dashboard");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredReferrers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return referrers;
    return referrers.filter((r) =>
      [r.name, r.email, r.phone, r.city, r.type, r.status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [referrers, search]);

  const filteredLocations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((l) =>
      [
        l.locationId,
        l.partnerId,
        l.referrerName,
        l.referrerId,
        l.status,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [locations, search]);

  const filteredEarnings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return earnings;
    return earnings.filter((e) =>
      [
        e.locationId,
        e.partnerId,
        e.referrerId,
        e.stripeSessionId,
        e.paymentStatus,
        e.payoutStatus,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [earnings, search]);

  if (loading) {
    return (
      <div className="radPage">
        <div className="radPageInner">
          <div className="radLoading">Loading referral dashboard…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="radPage">
      <div className="radPageInner">
        <div className="radTopbar">
          <div>
            <div className="radEyebrow">Admin</div>
            <h1 className="radH1">Referral Dashboard</h1>
            <p className="radMuted">
              Track location earnings, Pay2Pee earnings, referral commissions, and referrer performance.
            </p>
          </div>

          <div className="radTopbarActions">
            <input
              type="text"
              className="radSearch"
              placeholder="Search referrers, locations, earnings..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error ? <div className="radError">{error}</div> : null}

        <div className="radTabs">
          {["overview", "referrers", "locations", "earnings"].map((tab) => (
            <button
              key={tab}
              type="button"
              className={`radTab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab[0].toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === "overview" && overview ? (
          <>
            <div className="radStatsGrid">
              <StatCard label="Total Referrers" value={overview.kpis.totalReferrers} />
              <StatCard label="Active Referrers" value={overview.kpis.activeReferrers} />
              <StatCard label="Approved Locations" value={overview.kpis.approvedLocations} />
              <StatCard label="Earning Locations" value={overview.kpis.earningLocations} />
              <StatCard label="Gross Revenue" value={formatMoney(overview.kpis.totalGrossRevenue)} />
              <StatCard label="Pay2Pee Earnings" value={formatMoney(overview.kpis.totalPlatformEarnings)} />
              <StatCard label="Referral Commission" value={formatMoney(overview.kpis.totalReferralCommission)} />
              <StatCard label="Unpaid Commission" value={formatMoney(overview.kpis.unpaidReferralCommission)} />
            </div>

            <TableCard title="Recent Activity">
              <table className="radTable">
                <thead>
                  <tr>
                    <th>Paid At</th>
                    <th>Location</th>
                    <th>Referrer</th>
                    <th>Gross</th>
                    <th>Pay2Pee</th>
                    <th>Referral</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview.recentActivity || []).map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.paidAt)}</td>
                      <td>{row.locationId || "—"}</td>
                      <td>{row.referrerId || "—"}</td>
                      <td>{formatMoney(row.grossAmount)}</td>
                      <td>{formatMoney(row.platformFeeAmount)}</td>
                      <td>{formatMoney(row.referralCommissionAmount)}</td>
                      <td>{row.payoutStatus || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          </>
        ) : null}

        {activeTab === "referrers" ? (
          <TableCard title="Referrers">
            <table className="radTable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Email</th>
                  <th>City</th>
                  <th>Approved Locations</th>
                  <th>Platform Earnings</th>
                  <th>Commission Earned</th>
                  <th>Unpaid</th>
                </tr>
              </thead>
              <tbody>
                {filteredReferrers.map((r) => (
                  <tr key={r.id}>
                    <td>{r.name || "—"}</td>
                    <td>{r.status || "—"}</td>
                    <td>{r.type || "—"}</td>
                    <td>{r.email || "—"}</td>
                    <td>{r.city || "—"}</td>
                    <td>{r.totalApprovedLocations || 0}</td>
                    <td>{formatMoney(r.totalPlatformEarnings || 0)}</td>
                    <td>{formatMoney(r.totalCommissionEarned || 0)}</td>
                    <td>{formatMoney(r.totalCommissionUnpaid || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        ) : null}

        {activeTab === "locations" ? (
          <TableCard title="Location Referrals">
            <table className="radTable">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Partner</th>
                  <th>Referrer</th>
                  <th>Status</th>
                  <th>Window</th>
                  <th>Gross Revenue</th>
                  <th>Pay2Pee Earnings</th>
                  <th>Referral Owed</th>
                  <th>Active Days</th>
                  <th>Transactions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map((l) => (
                  <tr key={l.id}>
                    <td>{l.locationId || l.id}</td>
                    <td>{l.partnerId || "—"}</td>
                    <td>{l.referrerName || l.referrerId || "—"}</td>
                    <td>{l.status || "—"}</td>
                    <td>{l.currentWindowMonths || 0} mo</td>
                    <td>{formatMoney(l.grossRevenueTotal || 0)}</td>
                    <td>{formatMoney(l.platformEarningsTotal || 0)}</td>
                    <td>{formatMoney(l.unpaidCommissionTotal || 0)}</td>
                    <td>{l.activeDaysCount || 0}</td>
                    <td>{l.transactionCount || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        ) : null}

        {activeTab === "earnings" ? (
          <TableCard title="Payment Earnings Ledger">
            <table className="radTable">
              <thead>
                <tr>
                  <th>Paid At</th>
                  <th>Session</th>
                  <th>Location</th>
                  <th>Partner</th>
                  <th>Referrer</th>
                  <th>Gross</th>
                  <th>Pay2Pee</th>
                  <th>Referral</th>
                  <th>Payout Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredEarnings.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDate(e.paidAt)}</td>
                    <td>{e.stripeSessionId || e.id}</td>
                    <td>{e.locationId || "—"}</td>
                    <td>{e.partnerId || "—"}</td>
                    <td>{e.referrerId || "—"}</td>
                    <td>{formatMoney(e.grossAmount || 0)}</td>
                    <td>{formatMoney(e.platformFeeAmount || 0)}</td>
                    <td>{formatMoney(e.referralCommissionAmount || 0)}</td>
                    <td>{e.payoutStatus || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        ) : null}
      </div>
    </div>
  );
}